/* eslint-disable no-nested-ternary */
import { ThemeProvider } from '@emotion/react';
import {
  Box,
  Chip,
  CircularProgress,
  createTheme,
  Fade,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import BoltIcon from '@mui/icons-material/Bolt';
import LayersIcon from '@mui/icons-material/Layers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BRAND } from 'shared/Constants';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import { AIRiskBaselineService } from 'services/AIRiskBaselineService';
import { Toast, Utils } from 'shared/helpers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const RESPONSE_OPTIONS: any = [
  { label: 'Yes', scoringKey: 'Yes' },
  { label: 'Partly', scoringKey: 'Partly' },
  { label: 'No', scoringKey: 'No' },
  { label: "Don't know", scoringKey: 'DontKnow' },
];

const filterSectionsByMode = (sections: any, mode: string): any =>
  sections
    .filter((s: any) => s.isActive)
    .sort((a: any, b: any) => a.order - b.order)
    .map((section: any) => ({
      ...section,
      subsections: section.subsections
        .filter((sub: any) => sub.isActive)
        .sort((a: any, b: any) => a.order - b.order)
        .map((sub: any) => ({
          ...sub,
          questions: sub.questions
            .filter(
              (q: any) =>
                q.isActive &&
                (mode === 'Comprehensive' || q.mode === 'Essential')
            )
            .sort((a: any, b: any) => a.order - b.order),
        }))
        .filter((sub: any) => sub.questions.length > 0),
    }))
    .filter((s: any) => s.subsections.length > 0);

const OptionBox = ({ label, selected, onClick }: any): ReactElement => (
  <Box
    onClick={onClick}
    sx={{
      px: 2.5,
      py: 0.9,
      borderRadius: '50px',
      border: selected ? '2px solid white' : '1.5px solid #cfe870',
      background: selected ? '#c9ec6f' : 'white',
      color: selected ? '#fff' : '#374060',
      fontWeight: selected ? 700 : 500,
      fontSize: '0.8rem',
      cursor: 'pointer',
      userSelect: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 0.6,
      '&:hover': {
        background: selected ? '#e0f9a0' : '#f1f8e6',
        borderColor: '#c9ec6f',
      },
    }}
  >
    {selected && (
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          border: '2px solid #fff',
          background: '#4a5a1a',
          flexShrink: 0,
        }}
      />
    )}
    {label}
  </Box>
);

const QuestionBox = ({ question, response, onAnswer }: any): ReactElement => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      mb: 2,
      border: '1.5px solid #E5E7EB',
      background: response ? '#f9fafb' : '#fafbff',
      transition: 'border-color 0.2s',
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
      <Typography
        variant="caption"
        sx={{
          color: '#c9ec6f',
          fontWeight: 700,
          fontSize: '0.7rem',
          mt: 0.3,
          flexShrink: 0,
        }}
      >
        Q{question.id}
      </Typography>

      <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
        {question.text}
      </Typography>
    </Stack>

    <Stack direction="row" flexWrap="wrap" gap={1} mb={1.5}>
      {RESPONSE_OPTIONS.map((opt: { label: any }) => (
        <OptionBox
          key={opt.label}
          label={opt.label}
          selected={response === opt.label}
          onClick={() => onAnswer(question.id, opt.label)}
        />
      ))}
    </Stack>

    <Stack direction="row" gap={1} mt={1.5}>
      <Chip
        label={`${question.priority.toUpperCase()} PRIORITY`}
        size="small"
        sx={{
          fontSize: '0.6rem',
          fontWeight: 700,
          background: '#fff0f0',
          color: '#c0392b',
          border: 'none',
          height: 20,
        }}
      />

      <Chip
        label={question.mode.toUpperCase()}
        size="small"
        sx={{
          fontSize: '0.6rem',
          fontWeight: 700,
          background: question.mode === 'Essential' ? '#bfe45c' : '#4a5a1a',
          color: 'white',
          border: 'none',
          height: 20,
        }}
      />
    </Stack>
  </Paper>
);

const AIRiskBaselineSurvey = (): any => {
  const [mode, setMode] = useState<any>(null);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [responses, setResponses] = useState<any>({});
  const navigate = useNavigate();
  const location = useLocation();
  const [questionnaireData, setQuestionnaireData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [assessmentId, setAssessmentId] = useState<any>('');
  const [componentPurpose, setComponentPurpose] = useState<any>('');

  const filteredSections = useMemo(
    () =>
      questionnaireData?.sections
        ? filterSectionsByMode(questionnaireData.sections, mode)
        : [],
    [questionnaireData?.sections, mode]
  );

  const handlePayloadCreate = (isLastSection: any, mode: any): any => ({
    questionnaire_id: assessmentId,
    questionnaire_snapshot: {
      ...questionnaireData,
      sections: filteredSections.map((section: any) => ({
        ...section,
        subsections: section.subsections.map((sub: any) => ({
          ...sub,
          questions: sub.questions.map((q: any) => ({
            ...q,
            // response: responses[q.id] ?? null,
          })),
        })),
      })),
    },
    responses: Object.entries(responses).reduce<any>(
      (acc, [id, value]: any) => {
        acc[id] = value;
        return acc;
      },
      {}
    ),
    status: isLastSection ? 'completed' : 'in_progress',
    mode: mode.toLowerCase(),
  });

  const handlePayloadUpdate = (mode: any, isLastSection: any = null): any => ({
    responses: Object.entries(responses).reduce<any>(
      (acc, [id, value]: any) => {
        acc[id] = value;
        return acc;
      },
      {}
    ),
    status:
      isLastSection !== null
        ? isLastSection
          ? 'completed'
          : 'in_progress'
        : 'completed',
    mode: mode.toLowerCase(),
  });

  const isLastSection = sectionIndex >= filteredSections.length - 1;
  const currentSection = filteredSections[sectionIndex] ?? filteredSections[0];
  const currentQuestionIds: any[] = [];

  if (currentSection) {
    (currentSection?.subsections || []).forEach((s: any) => {
      (s?.questions || []).forEach((q: any) => {
        if (q?.id) {
          currentQuestionIds.push(q.id);
        }
      });
    });
  }

  const answeredCount = currentQuestionIds.filter(
    (id: any) => responses[id]
  ).length;
  const totalCount = currentQuestionIds.length;

  const allIds: any[] = [];

  filteredSections.forEach((s: any) => {
    s?.subsections.forEach((sub: any) => {
      sub?.questions.forEach((q: any) => {
        if (q?.id) {
          allIds.push(q.id);
        }
      });
    });
  });

  const totalAnswered = allIds.filter((id: any) => responses[id]).length;
  const progressPct = allIds.length ? (totalAnswered / allIds.length) * 100 : 0;

  const createAssessment = (data: any): void => {
    AIRiskBaselineService.createAssessment(data)
      .then(() => {
        if (isLastSection) {
          Toast.showSuccessMessage('Assessment complete');
          navigate(-1);
        } else {
          setSectionIndex((i) => i + 1);
        }
        setIsSaving(false);
      })
      .catch((error) => {
        setIsSaving(false);
        Utils.handleErrorResponse(error);
      });
  };

  const updateAssessment = (data: any): void => {
    AIRiskBaselineService.updateAssessment(assessmentId, data)
      .then(() => {
        if (isLastSection) {
          Toast.showSuccessMessage('Assessment complete');
          navigate(-1);
        } else {
          setSectionIndex((i) => i + 1);
        }
        setIsSaving(false);
      })
      .catch((error) => {
        setIsSaving(false);
        Utils.handleErrorResponse(error);
      });
  };

  const handleAnswer = (qId: number, value: string): void => {
    setResponses((prev: any) => ({ ...prev, [qId]: value }));
  };

  const handleModeChange = (_: any, newMode: any): void => {
    if (newMode) {
      setMode(newMode);
      setSectionIndex(0);
    }
  };

  const handleNext = (): void => {
    setIsSaving(true);
    if (componentPurpose === 'new') {
      createAssessment(handlePayloadCreate(isLastSection, mode));
    } else if (componentPurpose === 'completed') {
      updateAssessment(handlePayloadUpdate(mode));
    } else {
      updateAssessment(handlePayloadUpdate(mode, isLastSection));
    }
  };

  const handleBack = (): void => {
    if (sectionIndex > 0) setSectionIndex((i) => i - 1);
  };

  const handleSkip = (): void => handleNext();

  const theme = createTheme({
    typography: {
      h4: { fontWeight: 800 },
      subtitle1: { fontWeight: 500 },
    },
    shape: { borderRadius: 14 },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { boxShadow: '0 2px 16px 0 lightgray' },
        },
      },
    },
  });

  const handleGetAssessmentById = (dataId: any): void => {
    AIRiskBaselineService.getAssessmentById(dataId)
      .then((res) => {
        setComponentPurpose(res?.data?.data?.status);
        setMode(
          res?.data?.data?.mode === 'essential' ? 'Essential' : 'Comprehensive'
        );
        setAssessmentId(res?.data?.data?._id);

        const data = res?.data?.data;
        const savedResponses: any = data?.responses ?? {};

        const mergedSections = data?.questionnaire_snapshot?.sections?.map(
          (section: any) => ({
            ...section,
            subsections: section.subsections.map((sub: any) => ({
              ...sub,
              questions: sub.questions.map((q: any) => ({
                ...q,
                response: savedResponses[String(q.id)] ?? null,
              })),
            })),
          })
        );

        setResponses(savedResponses);

        setQuestionnaireData({
          ...data?.questionnaire_snapshot,
          sections: mergedSections,
        });
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  useEffect(() => {
    const paths = location.pathname.split('/').filter(Boolean);

    if (location.pathname.includes('/ai-risk-baseline/survey/') && paths[2]) {
      const dataId = paths[2];
      handleGetAssessmentById(dataId);
    } else if (location.state) {
      setComponentPurpose('new');
      console.log(location.state);
      setQuestionnaireData(
        location?.state?.questionnaireData?.[0]?.questionnaire_data
      );
      setMode(location?.state?.mode);
      setAssessmentId(location?.state?.questionnaireData?.[0]?._id);
    }
  }, [location.state]);

  if (!questionnaireData) {
    return (
      <Box className="empty-content" sx={{ height: '100vh' }}>
        <CircularProgress
          title="progress loading"
          className="mr-2 margin-loader"
          size={54}
          color="inherit"
        />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={{ initialResponse: filteredSections }}
      enableReinitialize
      onSubmit={() => console.log('submit', responses)}
    >
      {({ values }) => (
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: '100vh',
              background:
                'linear-gradient(316deg,rgba(209, 242, 122, 0.48) 0%, rgba(226, 249, 160, 0.56) 60%, rgba(240, 255, 208, 0.45) 100%)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 5 },
                py: 1.8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <AppPrimaryButton
                onClick={() => {
                  navigate(-1);
                  handleNext();
                }}
                sx={{
                  color: '#06362b !important',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'rgba(25, 118, 210, 0.04) !important',
                  },
                  '&:disabled': { opacity: 0.3 },
                  backgroundColor: 'transparent !important',
                  textDecoration: 'underline',
                }}
                title="Save & Exit"
                variant="text"
              />

              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeChange}
                sx={{
                  background: '#fff',
                  borderRadius: '50px',
                  overflow: 'hidden',
                  border: '1.5px solid #4a5a1a',
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '50px !important',
                    px: 2.5,
                    py: 0.7,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#666',
                    '&.Mui-selected': {
                      background: '#fff',
                      color: '#4a5a1a',
                      boxShadow: '0 2px 8px rgba(30,50,120,0.12)',
                    },
                  },
                }}
              >
                <ToggleButton value="Essential">
                  <BoltIcon sx={{ fontSize: 15, mr: 0.5 }} />
                  Essential
                </ToggleButton>
                <ToggleButton value="Comprehensive">
                  <LayersIcon sx={{ fontSize: 15, mr: 0.5 }} />
                  Comprehensive
                </ToggleButton>
              </ToggleButtonGroup>

              <Box
                sx={{
                  background: '#06362b',
                  borderRadius: '10px',
                  padding: '10px',
                }}
              >
                <img src={BRAND.LOGO} width={200} alt="Quixx Logo" />
              </Box>
            </Box>

            <Box sx={{ px: { xs: 2, md: 5 } }}>
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography
                  variant="caption"
                  sx={{ color: '#4a5a1a', fontWeight: 600 }}
                >
                  Section {sectionIndex + 1} of {values.initialResponse.length}{' '}
                  ({mode})
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#4a5a1a', fontWeight: 600 }}
                >
                  {answeredCount} / {totalCount} answered
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{
                  height: 5,
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.4)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #8cbf1a, #b8e030)',
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: { xs: 2, md: 5 },
                pt: 3,
                pb: 2,
              }}
            >
              {currentSection && (
                <Fade in key={`${mode}-${sectionIndex}`} timeout={300}>
                  <Box>
                    <Typography variant="h4" sx={{ mb: 0.5 }}>
                      {currentSection.title}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: '#7E7E7E', mb: 3 }}
                    >
                      NIST Functions: {currentSection.nistFunctions.join(', ')}
                    </Typography>

                    {currentSection.subsections.map((sub: any) => (
                      <Box key={sub.title} mb={3}>
                        <Typography
                          variant="overline"
                          sx={{
                            color: '#4a5a1a',
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            letterSpacing: '0.1em',
                            mb: 1.5,
                            display: 'block',
                          }}
                        >
                          {sub.title}
                        </Typography>
                        {sub.questions.map((q: any) => (
                          <QuestionBox
                            key={q.id}
                            question={q}
                            response={responses[q.id]}
                            onAnswer={handleAnswer}
                          />
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Fade>
              )}
            </Box>

            <Box
              sx={{
                px: { xs: 2, md: 5 },
                py: 2.5,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                borderTop: '1px solid rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(20px)',
              }}
            >
              <AppPrimaryButton
                onClick={handleBack}
                disabled={sectionIndex === 0}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: '#06362b !important',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'rgba(25, 118, 210, 0.04) !important',
                  },
                  '&:disabled': { opacity: 0.3 },
                  backgroundColor: 'transparent !important',
                }}
                title="Back"
                variant="text"
              />

              {!isLastSection && (
                <AppPrimaryButton
                  onClick={handleSkip}
                  endIcon={<SkipNextIcon />}
                  sx={{
                    color: '#06362b !important',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      background: 'rgba(25, 118, 210, 0.04) !important',
                    },
                    backgroundColor: 'transparent !important',
                  }}
                  title="Skip"
                  variant="text"
                />
              )}

              <AppPrimaryButton
                onClick={handleNext}
                variant="contained"
                endIcon={
                  isLastSection ? <CheckCircleIcon /> : <ArrowForwardIcon />
                }
                sx={{
                  background: '#073c30',
                  color: '#fff',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 3.5,
                  py: 1.2,
                  borderRadius: '50px',
                  fontSize: '0.9rem',
                  '&:hover': { background: '#052f25' },
                  '&:disabled': { opacity: 0.3 },
                }}
                title={isLastSection ? 'Complete Assessment' : 'Next'}
                disabled={isSaving}
                isLoading={isSaving}
                loadingText="Saving..."
              />
            </Box>
          </Box>
        </ThemeProvider>
      )}
    </Formik>
  );
};

export default AIRiskBaselineSurvey;
